import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import th from "date-fns/locale/th";
import moment from "moment";
import "moment/locale/th";

registerLocale("th", th);
moment.locale("th");

export default function AddInvoiceModal({ isOpen, onClose, invoiceData, onConfirm }) {
  const [formData, setFormData] = useState({
    invoiceDate: null,
    dueDate: null,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && invoiceData) {
      // ตั้งค่าวันที่ออก Invoice เป็นวันที่ออก Invoice เดิม + 1 วัน
      let invoiceDate = new Date();
      
      // ถ้ามี CT_Due_Date (วันที่ออก Invoice เดิม) ให้ใช้ + 1 วัน
      if (invoiceData.CT_Due_Date) {
        invoiceDate = new Date(invoiceData.CT_Due_Date);
        invoiceDate.setDate(invoiceDate.getDate() + 1);
      } else if (invoiceData.Date && invoiceData.Date !== "-") {
        // ถ้าไม่มี CT_Due_Date แต่มี Date (format DD/MM/YYYY)
        try {
          const parts = invoiceData.Date.split("/");
          if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10);
            let year = parseInt(parts[2], 10);
            // ถ้าเป็นปี พ.ศ. (มากกว่า 2500) แปลงเป็น ค.ศ.
            if (year > 2500) {
              year = year - 543;
            }
            invoiceDate = new Date(year, month - 1, day);
            invoiceDate.setDate(invoiceDate.getDate() + 1);
          }
        } catch (error) {
          console.error("Error parsing date:", error);
          invoiceDate = new Date();
          invoiceDate.setDate(invoiceDate.getDate() + 1);
        }
      } else {
        // ถ้าไม่มีข้อมูลเลย ให้ใช้วันปัจจุบัน + 1 วัน
        invoiceDate = new Date();
        invoiceDate.setDate(invoiceDate.getDate() + 1);
      }
      
      // ตั้งค่าวันที่กำหนดชำระเป็น 30 วันจากวันที่ออก Invoice ใหม่
      const dueDate = new Date(invoiceDate);
      dueDate.setDate(dueDate.getDate() + 30);
      
      setFormData({
        invoiceDate: invoiceDate,
        dueDate: dueDate,
      });
    }
  }, [isOpen, invoiceData]);

  const handleInvoiceDateChange = (date) => {
    if (date) {
      setFormData((prev) => ({
        ...prev,
        invoiceDate: date,
        // อัพเดทวันที่กำหนดชำระให้เป็น 30 วันจากวันที่ออก Invoice
        dueDate: (() => {
          const newDueDate = new Date(date);
          newDueDate.setDate(newDueDate.getDate() + 30);
          return newDueDate;
        })(),
      }));
    }
  };

  const handleDueDateChange = (date) => {
    if (date) {
      setFormData((prev) => ({
        ...prev,
        dueDate: date,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.invoiceDate) {
      alert("กรุณาเลือกวันที่ออก Invoice");
      return;
    }

    if (!formData.dueDate) {
      alert("กรุณาเลือกวันที่กำหนดชำระ");
      return;
    }

    if (formData.dueDate < formData.invoiceDate) {
      alert("วันที่กำหนดชำระต้องไม่เร็วกว่าวันที่ออก Invoice");
      return;
    }

    try {
      setLoading(true);
      await onConfirm({
        invoiceDate: moment(formData.invoiceDate).format("YYYY-MM-DD"),
        dueDate: moment(formData.dueDate).format("YYYY-MM-DD"),
      });
      onClose();
    } catch (error) {
      console.error("Error:", error);
      alert(error.message || "เกิดข้อผิดพลาด");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const remainingAmount = invoiceData?.remainingAmount || 0;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9555]">
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg relative">
        {/* Header */}
        <div className="flex items-center gap-2 px-6 py-4 bg-white border-b">
          <Icon icon="mdi:file-document-plus" className="text-[#E13F57] w-6 h-6" />
          <h3 className="font-bold text-lg text-[#E13F57]">สร้าง Invoice ใหม่</h3>
          <button
            onClick={onClose}
            className="ml-auto text-gray-500 hover:text-gray-700 text-xl px-2"
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* ข้อมูล Invoice เดิม */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              <strong>Invoice เดิม:</strong> {invoiceData?.Invoice_No || "-"}
            </p>
            <p className="text-sm text-gray-600 mb-2">
              <strong>ยอดที่ต้องชำระ:</strong> {Number(invoiceData?.ToPay || 0).toLocaleString()} บาท
            </p>
            <p className="text-sm text-gray-600 mb-2">
              <strong>ยอดที่ชำระแล้ว:</strong> {Number(invoiceData?.Paid || 0).toLocaleString()} บาท
            </p>
            <p className="text-sm font-semibold text-[#E13F57]">
              <strong>ยอดที่เหลือ:</strong> {remainingAmount.toLocaleString()} บาท
            </p>
          </div>

          {/* วันที่ออก Invoice */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              วันที่ออก Invoice <span className="text-red-500">*</span>
            </label>
            <DatePicker
              selected={formData.invoiceDate}
              onChange={handleInvoiceDateChange}
              dateFormat="dd/MM/yyyy"
              locale="th"
              placeholderText="เลือกวันที่ออก Invoice"
              className="w-full border rounded px-3 py-2 h-[42px]"
              wrapperClassName="w-full"
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              yearDropdownItemNumber={100}
              scrollableYearDropdown
              required
            />
          </div>

          {/* วันที่กำหนดชำระ */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              กำหนดชำระภายในวันที่ <span className="text-red-500">*</span>
            </label>
            <DatePicker
              selected={formData.dueDate}
              onChange={handleDueDateChange}
              dateFormat="dd/MM/yyyy"
              locale="th"
              placeholderText="เลือกวันที่กำหนดชำระ"
              className="w-full border rounded px-3 py-2 h-[42px]"
              wrapperClassName="w-full"
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              yearDropdownItemNumber={100}
              scrollableYearDropdown
              required
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-[#E13F57] text-white rounded hover:bg-[#c9354a] disabled:opacity-50"
              disabled={loading}
            >
              {loading ? "กำลังสร้าง..." : "สร้าง Invoice"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

